import * as React from "react";
import { v4 as uuidv4 } from "uuid";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Contract } from "../types";
import { api } from "../api";
import { useShowToast } from "../contexts/ToastContext.tsx";
import { extractApiError } from "../api/errors.ts";
import { useAppDispatch } from "../redux/store.ts";

type DownloadOption =
  | "existing"
  | "template"
  | "template-replace"
  | "template-replace-empty";

export const DownloadContract = ({
  contractData,
}: {
  contractData: Contract;
}) => {
  const showToast = useShowToast();
  const [showDialog, setShowDialog] = React.useState(false);
  const dispatch = useAppDispatch();
  const [triggerGetShortLivedJWT] =
    api.endpoints.getResourceTokenApiResourceTokenResourceResourceIdPost.useMutation();

  const downloadFile = React.useCallback(
    async (option: DownloadOption) => {
      const response = await triggerGetShortLivedJWT({
        resource: "Contract",
        resourceId: `${contractData.id}`,
      });
      if (response.error) {
        console.error(response.error);
        showToast({
          severity: "error",
          summary: "Błąd",
          detail: extractApiError(response.error),
        });
        return;
      }
      const { data } = response;
      if (!data) {
        return;
      }
      const { access_token: shortLivedToken } = data;
      const cacheBuster = uuidv4();

      let url: string;
      let shouldInvalidateHistory: boolean = false;
      switch (option) {
        case "existing":
          url = `/api/contracts/${contractData.id}/download?s=${shortLivedToken}&c=${cacheBuster}`;
          break;
        case "template":
          url = `/api/contracts/${contractData.id}/download_template?s=${shortLivedToken}&c=${cacheBuster}`;
          break;
        case "template-replace":
          url = `/api/contracts/${contractData.id}/download_template?s=${shortLivedToken}&c=${cacheBuster}&replace_existing_file=true`;
          shouldInvalidateHistory = true;
          break;
        case "template-replace-empty":
          url = `/api/contracts/${contractData.id}/download_template?s=${shortLivedToken}&c=${cacheBuster}&replace_if_empty_file=true`;
          shouldInvalidateHistory = true;
          break;
      }

      window.open(url, "_blank");
      if (shouldInvalidateHistory) {
        dispatch(api.util.invalidateTags(["history"]));
      }
      setShowDialog(false);
    },
    [showToast, contractData.id, triggerGetShortLivedJWT, dispatch],
  );

  const onDownload = React.useCallback(async () => {
    if (contractData.fileExists && contractData.canDownload) {
      setShowDialog(true);
    } else {
      if (contractData.canDownload) {
        await downloadFile("template-replace-empty");
      } else {
        await downloadFile("existing");
      }
    }
  }, [contractData.fileExists, contractData.canDownload, downloadFile]);

  const handleDialogHide = React.useCallback(() => {
    setShowDialog(false);
  }, []);

  if (!contractData.canDownload && !contractData.fileExists) {
    return null;
  }

  return (
    <>
      <Button
        icon="pi pi-file-word"
        text
        size="small"
        aria-label="Pobierać"
        title="Pobierać"
        onClick={() => {
          void onDownload();
        }}
      />
      <Dialog
        header="Wybierz opcję pobierania"
        visible={showDialog}
        onHide={handleDialogHide}
        style={{ width: "500px" }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Button
            label="Pobierz istniejący plik"
            icon="pi pi-download"
            onClick={() => {
              void downloadFile("existing");
            }}
            outlined
          />
          <Button
            label="Pobierz plik z szablonu"
            icon="pi pi-file-word"
            onClick={() => {
              void downloadFile("template");
            }}
            outlined
          />
          <Button
            label="Pobierz plik z szablonu i zastąp zapisany plik"
            icon="pi pi-file-export"
            onClick={() => {
              void downloadFile("template-replace");
            }}
            outlined
          />
        </div>
      </Dialog>
    </>
  );
};
